import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { authApi } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthProvider";

/**
 * /admin/auth-callback
 *
 * Emergent OAuth redirects the user back to this route with the session
 * id encoded in the URL FRAGMENT (`#session_id=...`), not the query
 * string. Fragments are stripped by browsers before hitting the server,
 * so this component runs entirely client-side.
 *
 * Responsibilities:
 *   1. Parse `session_id` out of the fragment
 *   2. POST it to /api/auth/session to exchange for a persistent cookie
 *   3. Refresh the AuthProvider state
 *   4. Navigate to `?next=` (defaults to /admin) — replace, not push,
 *      so the back button doesn't loop the user through this route
 *
 * Error UX: if anything fails (missing fragment, expired session_id,
 * network) we surface a calm message with a "Try again" link back to
 * the sign-in URL. NOT a full 500 page — the odds of the OAuth token
 * having briefly expired are non-zero.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, signInUrl } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Parse the URL fragment. Emergent puts session_id there so it
      // never touches the server. We use useLocation().hash so React
      // Router semantics (client-side navigation) work — reading
      // window.location.hash directly would miss mid-session redirects.
      const rawHash = location.hash || "";
      const params = new URLSearchParams(
        rawHash.startsWith("#") ? rawHash.slice(1) : rawHash,
      );
      const sessionId = params.get("session_id");
      const query = new URLSearchParams(location.search);
      const next = query.get("next") || "/admin";

      if (!sessionId) {
        if (!cancelled) setError("Missing session_id \u2014 please try signing in again.");
        return;
      }
      try {
        await authApi.exchangeSession(sessionId);
        await refresh();
        if (!cancelled) {
          // Strip the fragment before navigating so the user's URL bar
          // doesn't carry the session_id around the app.
          navigate(next, { replace: true });
        }
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail || "";
          setError(
            status === 401 || /expired|invalid/i.test(detail)
              ? "Your sign-in link expired. Please try again."
              : detail || "We couldn't complete sign-in. Please try again.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.hash, location.search, navigate, refresh]);

  return (
    <div
      data-testid="auth-callback-page"
      className="min-h-screen flex items-center justify-center px-5 py-24 bg-background"
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-[28px] border border-border/70 bg-secondary/25 backdrop-blur px-7 sm:px-10 py-10 sm:py-12 text-center"
      >
        {error ? (
          <>
            <p className="font-mono-display text-[10px] uppercase tracking-[0.32em] text-destructive mb-3">
              (Sign-in error)
            </p>
            <h1
              data-testid="auth-callback-error-heading"
              className="font-display text-2xl leading-tight"
            >
              {error}
            </h1>
            <a
              href={signInUrl()}
              data-testid="auth-callback-retry-link"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-foreground text-background px-5 py-3 min-h-[44px]"
            >
              <span className="font-mono-display text-[11px] uppercase tracking-[0.28em]">
                Try again
              </span>
            </a>
          </>
        ) : (
          <>
            <p className="font-mono-display text-[10px] uppercase tracking-[0.32em] text-foreground/60 mb-3">
              (Signing you in)
            </p>
            <h1 className="font-display text-2xl leading-tight">
              Just a moment&hellip;
            </h1>
            <p className="mt-3 text-sm text-foreground/60">
              Completing your Google sign-in.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
